/**
 * <SuppliersTable>
 *
 * Suppliers 回复列表表格。基于 Figma 节点 9281-13918 还原。
 * 使用 @leadsnavi/ui-kit Table 组件保持风格统一。
 *
 * 列定义（与 Figma 1:1）：
 *   Company(220) | Name(120) | Email(160) | Status(138) | Details(160) | Unit Price(80) |
 *   Total Quotation(102) | Delivery(100) | Additional Info(160) | Payment(100) | AI Reply(80) ← pinned | View Reply ← pinned
 *
 * 最后两列（AI Reply / View Reply）固定在右侧（sticky），横向滚动不受影响。
 *
 * 空态：显示空状态插图 + "No Tasks yet" 文案
 */

import { FC, useCallback } from 'react';
import cn from 'classnames';
import { ExternalLink } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@leadsnavi/ui-kit';
import { BasePagination } from '@/common/base/pagination';
import { Ellipse } from '@/common/base/ellipse';
import type { SupplierRowVM, SuppliersTableVM } from '../../types';
import StatusBadge from './status-badge';
import AiReplySwitch from './ai-reply-switch';
import NoTasksEmptyIcon from '../../assets/no-tasks-empty.svg?react';
import HourglassPlaceholderIcon from '../../assets/hourglass-placeholder.svg?react';
import { EMPTY_PLACEHOLDER } from '../../lib/view-model';

// ---------------------------------------------------------------------------
// 常量
// ---------------------------------------------------------------------------

/** 总列数（含 pinned） */
const TOTAL_COLUMNS = 12;

/** 右侧固定列样式：共用 sticky + 白色背景 + 阴影分割线 */
const STICKY_COL_BASE = 'sticky z-10 bg-white';
/** View Reply 列宽 */
const VIEW_REPLY_W = 100;
/** AI Reply 列宽 */
const AI_REPLY_W = 80;
/** View Reply 列 right offset */
const VIEW_REPLY_RIGHT = 0;
/** AI Reply 列 right offset = View Reply 宽度 */
const AI_REPLY_RIGHT = VIEW_REPLY_W;

export type SuppliersTableProps = {
  vm: SuppliersTableVM;
  loading?: boolean;
  onPageChange: (page: number) => void;
  onToggleAiReply: (id: string, next: boolean) => Promise<void>;
  onClickDetails: (row: SupplierRowVM) => void;
  onCompanyClick?: (row: SupplierRowVM) => void;
  className?: string;
};

/** 缺失字段占位：若值为 EMPTY_PLACEHOLDER 则渲染沙漏图标，否则渲染原文本 */
const FieldCell: FC<{ value: string; ellipsis?: boolean }> = ({ value, ellipsis = false }) => {
  if (value === EMPTY_PLACEHOLDER) return <HourglassPlaceholderIcon className="w-3 h-3" />;
  if (ellipsis) return <Ellipse text={value} className="text-sm" />;
  return <>{value}</>;
};

export const SuppliersTable: FC<SuppliersTableProps> = ({
  vm,
  loading = false,
  onPageChange,
  onToggleAiReply,
  onClickDetails,
  onCompanyClick,
  className,
}) => {
  const handleToggle = useCallback((id: string, next: boolean) => onToggleAiReply(id, next), [onToggleAiReply]);

  const { rows, page, pageSize, total } = vm;
  const isEmpty = rows.length === 0 && !loading;

  /** 行背景色，pinned 列也要继承 */
  const rowBg = (idx: number) => (idx % 2 === 1 ? 'bg-[#F8F8F7]' : 'bg-white');

  return (
    <section className={cn('w-full overflow-x-auto', className)}>
      <Table className="w-full table-fixed min-w-[1560px]">
        <TableHeader>
          <TableRow className="bg-[#FAFAFA] border-b border-[#E5E5E5] h-[66px]">
            <TableHead className="w-[220px] px-4 py-2 text-left text-sm font-medium text-[#737373]">Company</TableHead>
            <TableHead className="w-[120px] px-4 py-2 text-left text-sm font-medium text-[#737373]">Name</TableHead>
            <TableHead className="w-[160px] px-4 py-2 text-left text-sm font-medium text-[#737373]">Email</TableHead>
            <TableHead className="w-[138px] px-4 py-2 text-left text-sm font-medium text-[#737373]">Status</TableHead>
            <TableHead className="w-[160px] px-4 py-2 text-left text-sm font-medium text-[#737373]">Details</TableHead>
            <TableHead className="w-[120px] px-4 py-2 text-left text-sm font-medium text-[#737373]">
              Unit Price
            </TableHead>
            <TableHead className="w-[120px] px-4 py-2 text-left text-sm font-medium text-[#737373]">
              Total Quotation
            </TableHead>
            <TableHead className="w-[130px] px-4 py-2 text-left text-sm font-medium text-[#737373]">Delivery</TableHead>

            <TableHead className="w-[100px] px-4 py-2 text-left text-sm font-medium text-[#737373]">Payment</TableHead>
            <TableHead className="w-[160px] px-4 py-2 text-left text-sm font-medium text-[#737373]">
              Additional Info
            </TableHead>
            {/* ─── Pinned right columns ─── */}
            <TableHead
              className={cn(
                `w-[${AI_REPLY_W}px] px-4 py-2 text-left text-sm font-medium text-[#737373]`,
                STICKY_COL_BASE,
                'bg-[#FAFAFA] shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]'
              )}
              style={{ right: AI_REPLY_RIGHT, width: AI_REPLY_W }}
            >
              AI Reply
            </TableHead>
            {/* 最后一列无表头，放 View Reply */}
            <TableHead
              className={cn('w-[100px] px-4 py-2', STICKY_COL_BASE, 'bg-[#FAFAFA]')}
              style={{ right: VIEW_REPLY_RIGHT, width: VIEW_REPLY_W }}
            />
          </TableRow>
        </TableHeader>
        <TableBody>
          {/* 空态 */}
          {isEmpty && (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={TOTAL_COLUMNS} className="py-0 border-none ">
                <div className="flex flex-col items-center justify-center py-16">
                  <NoTasksEmptyIcon className="w-[208px] h-[107px] mb-2" />
                  <span className="text-xs text-[#737373] tracking-wider text-center">
                    No response received yet. Please be patient while waiting for a reply.
                  </span>
                </div>
              </TableCell>
            </TableRow>
          )}

          {/* 数据行 */}
          {rows.map((row, idx) => (
            <TableRow key={row.id} className={cn('hover:bg-gray-50 transition-colors', rowBg(idx))}>
              {/* Company: avatar + name + ExternalLink */}
              <TableCell className="px-4 py-4">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-[14px] bg-[#F5F5F5] border-[0.58px] border-[#E5E5E5] shrink-0 flex items-center justify-center overflow-hidden">
                    <span className="text-[10px] font-medium text-[#737373]">
                      {row.companyName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 min-w-0 overflow-hidden group/company">
                    <span
                      className="text-sm text-[#262626] group-hover/company:text-[#2563EB] truncate cursor-pointer transition-colors"
                      title={row.companyName}
                      onClick={() => onCompanyClick?.(row)}
                    >
                      {row.companyName}
                    </span>
                    <ExternalLink
                      size={16}
                      className="shrink-0 text-[#737373] group-hover/company:text-[#2563EB] cursor-pointer transition-colors"
                      onClick={() => onCompanyClick?.(row)}
                    />
                  </div>
                </div>
              </TableCell>

              {/* Name */}
              <TableCell className="px-4 py-4 text-sm text-[#262626]">
                <span className="truncate block" title={row.contactName}>
                  {row.contactName}
                </span>
              </TableCell>

              {/* Email */}
              <TableCell className="px-4 py-4 text-sm text-[#262626]">
                <FieldCell value={row.email} ellipsis />
              </TableCell>

              {/* Status */}
              <TableCell className="px-4 py-4">
                <StatusBadge status={row.status} label={row.statusLabel} />
              </TableCell>

              {/* Details */}
              <TableCell className="px-4 py-4">
                <FieldCell value={row.detailsText} ellipsis />
              </TableCell>

              {/* Unit Price */}
              <TableCell className="px-4 py-4 text-sm text-[#262626]">
                <FieldCell value={row.unitPrice} ellipsis />
              </TableCell>

              {/* Total Quotation */}
              <TableCell className="px-4 py-4 text-sm text-[#262626]">
                <FieldCell value={row.totalQuotation} ellipsis />
              </TableCell>

              {/* Delivery */}
              <TableCell className="px-4 py-4 text-sm text-[#262626]">
                <FieldCell value={row.delivery} ellipsis />
              </TableCell>

              {/* Payment */}
              <TableCell className="px-4 py-4 text-sm text-[#262626]">
                <FieldCell value={row.payment} ellipsis />
              </TableCell>

              {/* Additional Info */}
              <TableCell className="px-4 py-4 text-sm text-[#262626]">
                <span className="truncate block" title={row.additionalInfo}>
                  <FieldCell value={row.additionalInfo} ellipsis />
                </span>
              </TableCell>

              {/* ─── Pinned right: AI Reply ─── */}
              <TableCell
                className={cn('px-4 py-4', STICKY_COL_BASE, rowBg(idx), 'shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]')}
                style={{ right: AI_REPLY_RIGHT, width: AI_REPLY_W }}
              >
                <AiReplySwitch id={row.id} enabled={row.aiReplyEnabled} onToggle={handleToggle} />
              </TableCell>

              {/* ─── Pinned right: View Reply（无表头列） ─── */}
              <TableCell
                className={cn('px-4 py-4 text-sm text-[#2563EB]', STICKY_COL_BASE, rowBg(idx))}
                style={{ right: VIEW_REPLY_RIGHT, width: VIEW_REPLY_W }}
              >
                <button
                  type="button"
                  onClick={() => onClickDetails(row)}
                  className="text-sm text-[#2563EB] hover:underline whitespace-nowrap cursor-pointer"
                >
                  View Reply
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination — 始终展示，与 Figma 对齐 */}
      <div className="flex justify-center py-4">
        <BasePagination page={page} total={total} pageSize={pageSize} onPageChange={onPageChange} />
      </div>
    </section>
  );
};

export default SuppliersTable;
